import React, { useEffect, useState } from "react";
import { HiOutlineSelector } from "react-icons/hi";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { BeatLoader } from "react-spinners";
import { usePageSettings } from "../../../contexts/PageSettingsContext";

// Custom sorting function to return the
const customSort = (isoCode) => (a, b) => {
	// Sort by order if both have the same iso_code
	if (a.iso_code === b.iso_code) {
		return a.order - b.order;
	}
	// If a has the specified iso_code, prioritize it
	if (a.iso_code === isoCode) {
		return -1;
	}
	// If b has the specified iso_code, prioritize it
	if (b.iso_code === isoCode) {
		return 1;
	}
	// Otherwise, sort by order
	return a.order - b.order;
};

const LocationListBox = ({ locationList = [], color = "black", loading, translations }) => {
	const { selectedLocation, changeSelectedLocation } = usePageSettings();
	const [sortedCountries, setSortedCountries] = useState([]);

	const handleLocationChange = (location) => {
		changeSelectedLocation(location);
	};

	useEffect(() => {
		if (locationList.length > 0 && selectedLocation.iso_code !== "") {
			// console.log("locationList:", locationList);
			// Filter locations based on the iso_code
			const filteredCountries = locationList.filter(
				(location) => location.iso_code === selectedLocation.iso_code
			);
			const remainingCountries = locationList.filter(
				(location) => location.iso_code !== selectedLocation.iso_code
			);
			// Sort the filtered (selected) countries by order
			const sortedFilteredCountries = filteredCountries.sort((a, b) => a.order - b.order);
			// Sort the remaining countries using the custom sorting function
			const sortedRemainingCountries = remainingCountries.sort(customSort(selectedLocation.iso_code));
			// Concatenate the filtered and sorted countries
			const sortedCountries = sortedFilteredCountries.concat(sortedRemainingCountries);
			// console.log("locationList (sorted):", sortedCountries);
			setSortedCountries(sortedCountries);
		}
		return () => {
			// This is an empty cleanup function.
		};
	}, [locationList, selectedLocation]);

	// Determine the CSS class names based on the 'color' prop
	const optionsClassName =
		color === "black" ? "location-listbox-options-primary" : "location-listbox-options-secondary";

	const optionClassName =
		color === "black"
			? "location-listbox-option-primary hover:text-[#f3f4f6] hover:bg-[#2d3335]"
			: "location-listbox-option-secondary hover:bg-zinc-200";

	return (
		<div className="md:flex self-start relative flex-grow w-full">
			<Listbox value={selectedLocation} onChange={handleLocationChange}>
				<LocationListBoxTitle
					locationName={translations?.[selectedLocation.name] || selectedLocation.name}
					locationFlag={selectedLocation.flag}
					color={color}
					loading={loading}
				/>
				<ListboxOptions
					className={`max-h-[9rem] absolute flex top-10 flex-col z-10 overflow-y-scroll shadow-2xl rounded-lg ${optionsClassName} border-white border-opacity-10 border mt-2`}
				>
					{sortedCountries
						? sortedCountries.map((location) => (
								<ListboxOption
									key={location.iata_code}
									value={location}
									disabled={location === selectedLocation}
								>
									{({ active, selected }) => {
										return (
											<span
												className={`text-xs flex gap-4 ${optionClassName} 
														${
															selected &&
															(color === "black"
																? "location-listbox-option-selected-primary"
																: "location-listbox-option-selected-secondary")
														} 
														${active && "location-listbox-option-active"}
														${
															location.iata_code === selectedLocation.iataCode &&
															`location-listbox-option-selected cursor-default ${
																color === "black"
																	? "location-listbox-option-selected-primary"
																	: "location-listbox-option-selected-secondary"
															}`
														}`}
											>
												<img
													className="h-5 p-0.5"
													src={"data:image/jpg;base64, " + location.flag}
													alt={"Flag of" + location.name?.split(",")[1]}
												/>
												<span className="w-full text-start 2xl:text-center truncate uppercase">
													{translations?.[location.name] || location.name}
												</span>
												<span className="w-5" />
											</span>
										);
									}}
								</ListboxOption>
						  ))
						: null}
				</ListboxOptions>
			</Listbox>
		</div>
	);
};

export default LocationListBox;

const LocationListBoxTitle = ({ locationName, locationFlag, color, loading }) => (
	<ListboxButton
		id="listbox-button"
		className={`flex justify-around md:min-w-[250px] xl:min-w-[250px] 2xl:w-full w-full items-center cursor-pointer rounded-full gap-3 p-2 px-5 text-xs shadow-inner border border-ipxonGray ${
			color === "white"
				? "bg-white border-opacity-10 text-ipxon-black"
				: "bg-ipxon-gradient-black-lighter border-ipxon-blue-gray"
		}`}
	>
		{loading ? (
			<div className="flex justify-between w-full">
				<div className="h-5 w-8 bg-ipxon-medium-dark-gray animate-pulse" />
				<div className="h-5 p-0.5">
					<BeatLoader color="#fff" size={6} />
				</div>
				<span className="pointer-events-none">
					<HiOutlineSelector className="h-5 w-5 text-gray-400" aria-hidden="true" />
				</span>
			</div>
		) : (
			<>
				<img
					className="h-5 w-8 p-0.5"
					src={"data:image/jpg;base64, " + locationFlag}
					alt={locationName?.split(",")[1] + " flag"}
				/>
				<span className="w-full  text-center truncate uppercase">{locationName}</span>
				<span className="pointer-events-none">
					<HiOutlineSelector className="h-5 w-5 text-gray-400" aria-hidden="true" />
				</span>
			</>
		)}
	</ListboxButton>
);
